import React from "react";
import { Button, Modal } from "react-bootstrap";
import { formatDate } from "../utils/dateUtils";
import S3ImageLoader from "../../../../S3ImageLoader";
import "./BillingInvoiceModal.scss";

const SectionDivider = () => <div className="billing-invoice-divider" />;

const Money = ({ symbol, value }) => (
	<span>
		{symbol}
		{Number(value || 0).toFixed(2)}
	</span>
);

const SummaryRow = ({ label, value, symbol, bold = false, className = "" }) => (
	<tr className={bold ? "fw-bold" : ""}>
		<td className={className}>{label}</td>
		<td className={`text-end ${className}`}>
			<Money symbol={symbol} value={value} />
		</td>
	</tr>
);

const InvoiceSectionBillingRows = ({ t, symbol, billing = {}, posTaxType, isSameState, sectionLabel }) => {
	if (!billing?.lineSubtotal && !billing?.net_total) return null;

	const taxType = String(billing.tax_type || posTaxType || "gst").toLowerCase();
	const discountLabel =
		billing.discount_type === "percentage" && Number(billing.discount) > 0
			? `${t("Discount")} (${billing.discount}%)`
			: t("Discount");

	return (
		<>
			<SummaryRow
				label={sectionLabel ? `${sectionLabel} ${t("Subtotal")}` : t("Total Taxable Value")}
				value={billing.lineSubtotal}
				symbol={symbol}
			/>
			{billing.discountAmount > 0 && (
				<>
					<SummaryRow
						label={discountLabel}
						value={billing.discountAmount}
						symbol={`-${symbol}`}
					/>
					<SummaryRow
						label={t("Net Taxable Value")}
						value={billing.sub_total}
						symbol={symbol}
					/>
				</>
			)}
			{billing.taxAmount > 0 && (
				<>
					{taxType === "gst" && isSameState ? (
						<>
							{billing.CGST > 0 && (
								<SummaryRow
									label={`${t("CGST")} (${billing.cgstPercentage || 0}%)`}
									value={billing.CGST}
									symbol={`+${symbol}`}
								/>
							)}
							{billing.SGST > 0 && (
								<SummaryRow
									label={`${t("SGST")} (${billing.sgstPercentage || 0}%)`}
									value={billing.SGST}
									symbol={`+${symbol}`}
								/>
							)}
						</>
					) : taxType === "gst" ? (
						billing.IGST > 0 && (
							<SummaryRow
								label={`${t("IGST")} (${billing.tax || 0}%)`}
								value={billing.IGST}
								symbol={`+${symbol}`}
							/>
						)
					) : (
						billing.VAT > 0 && (
							<SummaryRow
								label={`${t("VAT")} (${billing.tax || 0}%)`}
								value={billing.VAT}
								symbol={`+${symbol}`}
							/>
						)
					)}
					<SummaryRow
						label={t("Total Tax")}
						value={billing.taxAmount}
						symbol={`+${symbol}`}
						bold
					/>
				</>
			)}
		</>
	);
};

export const BillingInvoiceModal = ({
	t,
	show,
	invoiceData,
	onHide,
	onClose,
	onPrint,
	pharmacyData,
}) => {
	if (!invoiceData) return null;

	const symbol = invoiceData.currencySymbol || "";
	const isSameState = invoiceData.isSameState;
	const posTaxType = invoiceData.taxType?.toLowerCase() || "gst";
	const isDark = document.body.classList.contains("dark-only");

	const hasPharmacyItems = invoiceData.pharmacy?.items?.length > 0;
	const hasServices = invoiceData.services?.items?.length > 0;
	const hasInvestigations = invoiceData.investigations?.items?.length > 0;

	return (
		<Modal
			className="invoice-modal billing-invoice-modal"
			show={show}
			onHide={onHide}
			scrollable
			centered
			size="lg"
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header>
				<Modal.Title>{t("Billing Invoice Receipt")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div
					id="invoice-content"
					className={isDark ? "pos-invoice bg-dark text-white" : "pos-invoice"}
					style={{ fontSize: "14px" }}
				>
					<div className="text-center mb-2">
						{pharmacyData?.logo ? (
							<S3ImageLoader
								className="m-0"
								fileName={pharmacyData.logo}
								style={{ height: "45px", marginBottom: 5 }}
								alt="profile"
								fallbackSrc={`${process.env.PUBLIC_URL}/logo.png`}
							/>
						) : (
							<img
								src={`${process.env.PUBLIC_URL}/logo.png`}
								alt="logo"
								style={{ height: "40px", marginBottom: 5 }}
							/>
						)}
						<p className="mt-1 mb-0 fw-bold">{pharmacyData?.facility_name}</p>
						<small className="text-muted">{pharmacyData?.contact_person?.address}</small>
						<br />
						<small className="text-muted">
							{pharmacyData?.contact_person?.city}, {pharmacyData?.contact_person?.country}
						</small>
						<br />
						<small>
							{t("Tel")}: {pharmacyData?.contact_person?.phone_code}{" "}
							{pharmacyData?.contact_person?.mobile}
						</small>
						{pharmacyData?.gstin && (
							<>
								<br />
								<small className="text-muted">GSTIN: {pharmacyData.gstin}</small>
							</>
						)}
					</div>

					<div className="d-flex align-items-center justify-content-between mb-2">
						<div>
							<small className="text-primary fw-bold">{t("BILLING INVOICE")}</small>
							<br />
							<small>
								{t("Invoice")}: <strong>{invoiceData.invoiceNo}</strong>
							</small>
							{invoiceData.prescriptionNo && (
								<>
									<br />
									<small>
										{t("Prescription")}: <strong>{invoiceData.prescriptionNo}</strong>
									</small>
								</>
							)}
						</div>
						<div className="text-end">
							<small>{formatDate(invoiceData.date)}</small>
						</div>
					</div>

					<SectionDivider />

					<div className="mb-2" style={{ fontSize: "12px" }}>
						<div>
							<small>
								{t("Patient")}: <strong>{invoiceData.customer}</strong>
							</small>
						</div>
						{invoiceData.healthId && (
							<div>
								<small>
									{t("File Number")}: <strong>{invoiceData.healthId}</strong>
								</small>
							</div>
						)}
						{invoiceData.doctorName && (
							<div>
								<small>
									{t("Doctor")}: <strong>{invoiceData.doctorName}</strong>
								</small>
							</div>
						)}
						{invoiceData.visitDate && (
							<div>
								<small>
									{t("Visit Date")}: <strong>{invoiceData.visitDate}</strong>
								</small>
							</div>
						)}
						{invoiceData.hospitalName && (
							<div>
								<small>
									{t("Hospital")}: <strong>{invoiceData.hospitalName}</strong>
								</small>
							</div>
						)}
					</div>

					<SectionDivider />

					{hasPharmacyItems && (
						<>
							<div className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
								{t("Pharmacy Medicines")}
							</div>
							<table style={{ width: "100%", fontSize: "12px", lineHeight: "1.3", marginBottom: "10px" }}>
								<thead>
									<tr style={{ borderBottom: "1px solid #ddd" }}>
										<th style={{ width: "45%", textAlign: "left" }}>{t("Item")}</th>
										<th style={{ width: "10%", textAlign: "center" }}>{t("Qty")}</th>
										<th style={{ width: "20%", textAlign: "right" }}>{t("Price")}</th>
										<th style={{ width: "25%", textAlign: "right" }}>{t("Total")}</th>
									</tr>
								</thead>
								<tbody>
									{invoiceData.pharmacy.items.map((item, index) => (
										<tr key={`${item._id}-${index}`} style={{ borderBottom: "1px dotted #ddd" }}>
											<td style={{ padding: "4px 0", wordBreak: "break-word" }}>
												<div style={{ fontWeight: 600 }}>{item.brandName}</div>
												{item.batchNumber && (
													<div style={{ fontSize: "11px", color: "#666" }}>
														{t("Batch")}: {item.batchNumber}
													</div>
												)}
												{item.taxPercentage > 0 && (
													<div style={{ fontSize: "11px", color: "#666" }}>
														{t("Tax")}: {item.taxPercentage}%
													</div>
												)}
											</td>
											<td style={{ textAlign: "center", padding: "4px 0" }}>{item.quantity}</td>
											<td style={{ textAlign: "right", padding: "4px 0" }}>
												<Money symbol={symbol} value={item.salePrice} />
											</td>
											<td style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>
												<Money symbol={symbol} value={item.lineTotal} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
							<table style={{ width: "100%", fontSize: "12px", marginBottom: "8px" }}>
								<tbody>
									<SummaryRow
										label={t("Pharmacy Total Taxable Value")}
										value={invoiceData.pharmacy.subtotal}
										symbol={symbol}
									/>
									{invoiceData.pharmacy.discount > 0 && (
										<>
											<SummaryRow
												label={`${t("Pharmacy Discount")}${
													invoiceData.pharmacy.billing?.discount_type === "percentage" &&
													Number(invoiceData.pharmacy.billing?.discount) > 0
														? ` (${invoiceData.pharmacy.billing.discount}%)`
														: ""
												}`}
												value={invoiceData.pharmacy.discount}
												symbol={`-${symbol}`}
											/>
											<SummaryRow
												label={t("Pharmacy Net Taxable Value")}
												value={invoiceData.pharmacy.netTaxable}
												symbol={symbol}
											/>
										</>
									)}
									{invoiceData.pharmacy.tax > 0 && (
										<>
											{posTaxType === "gst" ? (
												isSameState ? (
													<>
														{invoiceData.pharmacy.CGST > 0 && (
															<SummaryRow
																label={`${t("Pharmacy CGST")} (${invoiceData.CGSTPercentage || 0}%)`}
																value={invoiceData.pharmacy.CGST}
																symbol={`+${symbol}`}
															/>
														)}
														{invoiceData.pharmacy.SGST > 0 && (
															<SummaryRow
																label={`${t("Pharmacy SGST")} (${invoiceData.SGSTPercentage || 0}%)`}
																value={invoiceData.pharmacy.SGST}
																symbol={`+${symbol}`}
															/>
														)}
													</>
												) : (
													invoiceData.pharmacy.IGST > 0 && (
														<SummaryRow
															label={`${t("Pharmacy IGST")} (${invoiceData.IGSTPercentage || 0}%)`}
															value={invoiceData.pharmacy.IGST}
															symbol={`+${symbol}`}
														/>
													)
												)
											) : (
												invoiceData.pharmacy.VAT > 0 && (
													<SummaryRow
														label={`${t("Pharmacy VAT")} (${invoiceData.taxPercentage || 0}%)`}
														value={invoiceData.pharmacy.VAT}
														symbol={`+${symbol}`}
													/>
												)
											)}
											<SummaryRow
												label={t("Pharmacy Tax")}
												value={invoiceData.pharmacy.tax}
												symbol={`+${symbol}`}
												bold
											/>
										</>
									)}
									<SummaryRow
										label={t("Pharmacy Net Total")}
										value={invoiceData.pharmacy.netTotal}
										symbol={symbol}
										bold
									/>
								</tbody>
							</table>
							<SectionDivider />
						</>
					)}

					{hasServices && (
						<>
							<div className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
								{t("Consultation & Services")}
							</div>
							<table style={{ width: "100%", fontSize: "12px", marginBottom: "8px" }}>
								<thead>
									<tr style={{ borderBottom: "1px solid #ddd" }}>
										<th style={{ width: "45%", textAlign: "left" }}>{t("Item")}</th>
										<th style={{ width: "10%", textAlign: "center" }}>{t("Qty")}</th>
										<th style={{ width: "45%", textAlign: "right" }}>{t("Amount")}</th>
									</tr>
								</thead>
								<tbody>
									{invoiceData.services.consultationItems?.length > 0 && (
										<>
											<tr>
												<td colSpan={3} className="fw-semibold pt-1">
													{t("Consultation")}
												</td>
											</tr>
											{invoiceData.services.consultationItems.map((item, index) => (
												<tr key={`consultation-${index}`}>
													<td>{item.name}</td>
													<td style={{ textAlign: "center" }}>{item.quantity || 1}</td>
													<td className="text-end">
														<Money symbol={symbol} value={item.amount} />
													</td>
												</tr>
											))}
										</>
									)}
									{invoiceData.services.generalServiceItems?.length > 0 && (
										<>
											<tr>
												<td colSpan={3} className="fw-semibold pt-1">
													{t("Services")}
												</td>
											</tr>
											{invoiceData.services.generalServiceItems.map((item, index) => (
												<tr key={`service-${index}`}>
													<td>{item.name}</td>
													<td style={{ textAlign: "center" }}>{item.quantity || 1}</td>
													<td className="text-end">
														<Money symbol={symbol} value={item.amount} />
													</td>
												</tr>
											))}
										</>
									)}
									{!invoiceData.services.consultationItems?.length &&
										!invoiceData.services.generalServiceItems?.length &&
										invoiceData.services.items.map((item, index) => (
											<tr key={`service-${index}`}>
												<td>
													{item.name}
													{item.isConsultation ? ` (${t("Consultation")})` : ""}
												</td>
												<td style={{ textAlign: "center" }}>{item.quantity || 1}</td>
												<td className="text-end">
													<Money symbol={symbol} value={item.amount} />
												</td>
											</tr>
										))}
									<InvoiceSectionBillingRows
										t={t}
										symbol={symbol}
										billing={invoiceData.services.billing}
										posTaxType={posTaxType}
										isSameState={isSameState}
									/>
									<SummaryRow
										label={t("Services Grand Total")}
										value={invoiceData.services.total}
										symbol={symbol}
										bold
									/>
								</tbody>
							</table>
							<SectionDivider />
						</>
					)}

					{hasInvestigations && (
						<>
							<div className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
								{t("Lab Investigations")}
							</div>
							<table style={{ width: "100%", fontSize: "12px", marginBottom: "8px" }}>
								<thead>
									<tr style={{ borderBottom: "1px solid #ddd" }}>
										<th style={{ width: "45%", textAlign: "left" }}>{t("Test")}</th>
										<th style={{ width: "10%", textAlign: "center" }}>{t("Qty")}</th>
										<th style={{ width: "45%", textAlign: "right" }}>{t("Amount")}</th>
									</tr>
								</thead>
								<tbody>
									{invoiceData.investigations.items.map((item, index) => (
										<tr key={`investigation-${index}`}>
											<td>{item.name}</td>
											<td style={{ textAlign: "center" }}>{item.quantity || 1}</td>
											<td className="text-end">
												<Money symbol={symbol} value={item.amount} />
											</td>
										</tr>
									))}
									<InvoiceSectionBillingRows
										t={t}
										symbol={symbol}
										billing={invoiceData.investigations.billing}
										posTaxType={posTaxType}
										isSameState={isSameState}
									/>
									<SummaryRow
										label={t("Lab Grand Total")}
										value={invoiceData.investigations.total}
										symbol={symbol}
										bold
									/>
								</tbody>
							</table>
							<SectionDivider />
						</>
					)}

					<div className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
						{t("Billing Summary")}
					</div>
					<table style={{ width: "100%", fontSize: "12px" }}>
						<tbody>
							{hasPharmacyItems && (
								<SummaryRow
									label={t("Pharmacy Taxable Value")}
									value={invoiceData.summary.pharmacyTaxable}
									symbol={symbol}
								/>
							)}
							{hasServices && (
								<SummaryRow
									label={t("Consultation & Services Total")}
									value={invoiceData.summary.servicesTotal}
									symbol={symbol}
								/>
							)}
							{hasInvestigations && (
								<SummaryRow
									label={t("Lab Total")}
									value={invoiceData.summary.investigationsTotal}
									symbol={symbol}
								/>
							)}
							<SummaryRow
								label={t("Sub Total")}
								value={invoiceData.summary.subtotal}
								symbol={symbol}
							/>
							<tr className="fw-bold" style={{ borderTop: "2px solid #000", borderBottom: "2px solid #000" }}>
								<td>{t("Grand Total")}</td>
								<td className="text-end fw-bold fs-6">
									<Money symbol={symbol} value={invoiceData.summary.grandTotal} />
								</td>
							</tr>
							<SummaryRow label={t("Paid Amount")} value={invoiceData.summary.paid} symbol={symbol} />
							{invoiceData.summary.due > 0 && (
								<SummaryRow
									label={t("Due Amount")}
									value={invoiceData.summary.due}
									symbol={symbol}
									className="text-danger fw-bold"
								/>
							)}
							{invoiceData.summary.change > 0 && (
								<SummaryRow
									label={t("Change Amount")}
									value={invoiceData.summary.change}
									symbol={symbol}
									className="text-success fw-bold"
								/>
							)}
							{invoiceData.summary.due <= 0 && invoiceData.summary.change <= 0 && invoiceData.summary.paid > 0 && (
								<tr>
									<td colSpan={2} className="text-center text-success fw-bold">
										{t("Fully Paid")} ✓
									</td>
								</tr>
							)}
						</tbody>
					</table>

					<SectionDivider />

					<div className="mt-2" style={{ fontSize: "12px" }}>
						<div className="d-flex justify-content-between flex-wrap gap-2">
							<small>
								<strong>{t("Payment Method")}:</strong> {invoiceData.paymentType}
							</small>
							<small>
								<strong>{t("Status")}:</strong> {t(invoiceData.paymentStatusLabel || invoiceData.paymentStatus)}
							</small>
							<small>
								<strong>{t("Currency")}:</strong> {invoiceData.currency}
							</small>
						</div>
						{invoiceData.settledAsPaid && (
							<small className="d-block mt-1 text-muted">
								{t("Billing settled as paid")}
							</small>
						)}
					</div>

					<div className="text-center mt-3">
						<p style={{ margin: 0 }}>
							<strong>{t("Thank you for your business")}</strong>
						</p>
						<p style={{ margin: 0, fontSize: "11px" }}>
							{t("Please keep this receipt for your records")}
						</p>
						{invoiceData?.receiptQrData?.qrCodeImage && (
							<div className="mt-2">
								<img
									src={invoiceData.receiptQrData.qrCodeImage}
									alt="QR Code"
									style={{ width: "80px", height: "80px" }}
								/>
								<div>
									<small className="text-muted">{t("Scan for digital receipt")}</small>
								</div>
							</div>
						)}
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="primary" onClick={onPrint}>
					{t("Print")}
				</Button>
				<Button variant="danger" onClick={onClose}>
					{t("Close")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default BillingInvoiceModal;
